import { useMemo } from 'react';
import type { TableQueryRequest, TableQueryResult, ReferenceDisplayValue } from '@finance/contracts';

export interface TableDataSource {
  fetch(request: TableQueryRequest, signal?: AbortSignal): Promise<TableQueryResult>;
  subscribe?(
    request: TableQueryRequest,
    callback: (result: TableQueryResult) => void
  ): () => void;
  fetchReferences?(entityType: string, ids: string[]): Promise<Record<string, ReferenceDisplayValue>>;
}

function unwrapComparable(value: unknown): unknown {
  if (value instanceof Date) return value.getTime();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const record = value as Record<string, unknown>;
  return record.value ?? record.id ?? record.code ?? record.label ?? record.name ?? value;
}

function compareValues(a: unknown, b: unknown): number {
  const left = unwrapComparable(a);
  const right = unwrapComparable(b);
  if (left == null && right == null) return 0;
  if (left == null) return -1;
  if (right == null) return 1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right);
  return String(left).localeCompare(String(right));
}

function parseNumericComparable(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!/^[-+]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) return undefined;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function compareOrderedValues(a: unknown, b: unknown): number | undefined {
  const left = unwrapComparable(a);
  const right = unwrapComparable(b);
  if (left == null || right == null || left === '' || right === '') return undefined;

  const leftNumber = parseNumericComparable(left);
  const rightNumber = parseNumericComparable(right);
  if (leftNumber !== undefined && rightNumber !== undefined) {
    return leftNumber - rightNumber;
  }

  const leftString = String(left);
  const rightString = String(right);
  const dateLike = /^\d{4}-\d{2}-\d{2}(?:T|$)/;
  if (dateLike.test(leftString) && dateLike.test(rightString)) {
    const leftTime = Date.parse(leftString);
    const rightTime = Date.parse(rightString);
    if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) return leftTime - rightTime;
    return undefined;
  }

  return leftString.localeCompare(rightString);
}

function scalarEquals(a: unknown, b: unknown): boolean {
  const left = unwrapComparable(a);
  const right = unwrapComparable(b);
  if (left == null || right == null) return left == null && right == null;
  if (typeof left === 'boolean' || typeof right === 'boolean') {
    return String(left).toLowerCase() === String(right).toLowerCase();
  }
  return String(left) === String(right);
}

function isOnDate(value: unknown, filterValue: unknown): boolean {
  const rawValue = unwrapComparable(value);
  const rawFilter = unwrapComparable(filterValue);
  if (rawValue == null || rawFilter == null) return false;
  const left = String(rawValue);
  const right = String(rawFilter);

  // Date-only filters represent a calendar day. ISO datetimes represent an
  // exact instant because datetime-local is normalized to ISO before querying.
  if (/^\d{4}-\d{2}-\d{2}$/.test(right)) return left.slice(0, 10) === right;

  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) return leftTime === rightTime;
  return left === right;
}

function matchesOrdered(
  value: unknown,
  filterValue: unknown,
  predicate: (comparison: number) => boolean,
): boolean {
  const comparison = compareOrderedValues(value, filterValue);
  return comparison !== undefined && predicate(comparison);
}

function matchesFilter(row: Record<string, unknown>, filter: TableQueryRequest['filters'][number]): boolean {
  const value = row[filter.field];
  switch (filter.operator) {
    case 'contains':
      return String(value ?? '').toLowerCase().includes(String(filter.value ?? '').toLowerCase());
    case 'equals':
      return scalarEquals(value, filter.value);
    case 'notEquals':
      return !scalarEquals(value, filter.value);
    case 'startsWith':
      return String(value ?? '').toLowerCase().startsWith(String(filter.value ?? '').toLowerCase());
    case 'endsWith':
      return String(value ?? '').toLowerCase().endsWith(String(filter.value ?? '').toLowerCase());
    case 'gt':
      return matchesOrdered(value, filter.value, comparison => comparison > 0);
    case 'gte':
      return matchesOrdered(value, filter.value, comparison => comparison >= 0);
    case 'lt':
      return matchesOrdered(value, filter.value, comparison => comparison < 0);
    case 'lte':
      return matchesOrdered(value, filter.value, comparison => comparison <= 0);
    case 'between': {
      if (!Array.isArray(filter.value) || filter.value.length !== 2) return false;
      const lower = compareOrderedValues(value, filter.value[0]);
      const upper = compareOrderedValues(value, filter.value[1]);
      return lower !== undefined && upper !== undefined && lower >= 0 && upper <= 0;
    }
    case 'on':
      return isOnDate(value, filter.value);
    case 'before':
      return matchesOrdered(value, filter.value, comparison => comparison < 0);
    case 'after':
      return matchesOrdered(value, filter.value, comparison => comparison > 0);
    case 'isNull':
      return value == null || value === '';
    case 'isNotNull':
      return value != null && value !== '';
    case 'in': {
      const candidates = Array.isArray(filter.value) ? filter.value : [filter.value];
      return candidates.some(candidate => scalarEquals(value, candidate));
    }
    case 'notIn': {
      const candidates = Array.isArray(filter.value) ? filter.value : [filter.value];
      return candidates.every(candidate => !scalarEquals(value, candidate));
    }
    default:
      return false;
  }
}

export function runLocalTableQuery(
  rows: Record<string, unknown>[],
  request: TableQueryRequest,
): TableQueryResult {
  let result = [...rows];

  if (request.globalSearch?.query && request.globalSearch.fields.length > 0) {
    const query = request.globalSearch.query.toLowerCase();
    const fields = request.globalSearch.fields;
    result = result.filter((row) =>
      fields.some((field) => String(row[field] ?? '').toLowerCase().includes(query)),
    );
  }

  if (request.filters.length > 0) {
    result = result.filter((row) => request.filters.every((filter) => matchesFilter(row, filter)));
  }

  if (request.sort && request.sort.length > 0) {
    result = result.sort((a, b) => {
      let comparison = 0;
      for (const s of request.sort) {
        const cmp = compareValues(a[s.field], b[s.field]);
        if (cmp !== 0) {
          comparison = s.direction === 'asc' ? cmp : -cmp;
          break;
        }
      }
      return comparison;
    });
  }

  const total = result.length;
  let page = 0;
  let pageSize = total;
  if ('page' in request.pagination) {
    page = request.pagination.page;
    pageSize = request.pagination.pageSize;
  }
  const start = page * pageSize;
  const paginated = result.slice(start, start + pageSize);

  return {
    rows: paginated,
    pagination: { page, pageSize, total },
    dataTimestamp: Date.now(),
  };
}

export function createLocalDataSource(rows: Record<string, unknown>[]): TableDataSource {
  return {
    fetch: async (request: TableQueryRequest, signal?: AbortSignal) => {
      if (signal?.aborted) {
        throw new Error('Cancelled');
      }

      return runLocalTableQuery(rows, request);
    },
  };
}

export function useLocalDataSource(rows: Record<string, unknown>[]): TableDataSource {
  return useMemo(() => createLocalDataSource(rows), [rows]);
}
